import { useAuth0 } from '@auth0/auth0-react';
import { Backdrop, Box, CircularProgress, styled } from '@mui/material';
import { FunctionComponent, Suspense, useEffect } from 'react';
import { AuthorizationScreen } from '../components/AuthorizationScreen';
import Page from '../components/Page';
import { PendingBadge, StyledBadge } from '../components/PendingBadge';
import { StoreView } from '../components/StoreView';
import TabsControl from '../components/TabsControl';
import UserMenu from '../components/UserMenu';
import { AppView } from '../constants';
import {
  CartIcon,
  CartInactiveIcon,
  ConsumersIcon,
  ConsumersInactiveIcon,
} from '../icons';
import { useGetCartTypeToggle } from '../services/cartManager';
import {
  getImageUrl,
  useGetMerchantConfigurationOptions,
} from '../services/configManager';
import { addToContext } from '../services/contextManager';
import {
  OptionalTabs,
  useGetApprovingLender,
  useGetSdkDisplayed,
  useGetSdkLoading,
  useGetSelectedTab,
  useInitAppFullStory,
} from '../services/flowManager';
import { useGetCurrentModal } from '../services/modalManager';
import {
  getFirstLoadAfterLogin,
  setFirstLoadAfterLogin,
  useGetPinScreenOpen,
  useOpenPinScreen,
} from '../services/pinManager';
import { useGetTokenInterval } from '../services/tokenManager';
import { useGetShowError } from '../services/userManager';
import ConsumersTab from '../tabs/ConsumersTab';
import CreateCartTab from '../tabs/CreateCartTab';
import TransactionsTab from '../tabs/TransactionsTab';
import { useInitApplicationConfiguration } from '../services/configManagerNew';

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  color: '#fff',
  zIndex: theme.zIndex.drawer + 1,
}));

const StyledUserMenuBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
}));

const useSelectedTabToPageName = (selected: OptionalTabs) => {
  const cartTypeToggle = useGetCartTypeToggle();

  switch (selected) {
    case 'cart':
      return cartTypeToggle ? AppView.AdvancedCart : AppView.CreateCart;

    case 'customers':
      return AppView.Consumers;

    case 'pending':
      return AppView.Pending;

    case 'transactions':
      return AppView.Transactions;
  }
};

const Main: FunctionComponent = () => {
  const { user } = useAuth0();
  const sdkDisplayed = useGetSdkDisplayed();
  const sdkLoading = useGetSdkLoading();
  const pinScreenOpen = useGetPinScreenOpen();
  const currentModal = useGetCurrentModal();
  const {
    pendingTab: displayPendingTab,
    consumersTab,
    transactionsTab,
  } = useGetMerchantConfigurationOptions();
  const openPINScreen = useOpenPinScreen();
  const showError = useGetShowError();

  const selectedTab = useGetSelectedTab();
  const selectedTabToPageName = useSelectedTabToPageName(selectedTab);

  const pageName =
    !currentModal && !sdkDisplayed ? selectedTabToPageName : undefined;

  useInitAppFullStory();
  useGetTokenInterval();

  useEffect(() => {
    const firstLoadAfterLogin = getFirstLoadAfterLogin();
    if (!firstLoadAfterLogin) {
      openPINScreen(sdkDisplayed);
    } else {
      setFirstLoadAfterLogin(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      addToContext('userEmail', user.email);
    }
  }, [user]);

  if (showError) {
    throw new Error('Merchant store required');
  }

  useInitApplicationConfiguration();
  return (
    <>
      {!!pinScreenOpen ? (
        <AuthorizationScreen open={pinScreenOpen} />
      ) : (
        <>
          <Page
            pageName={pageName}
            showFooter={!sdkDisplayed}
            disableStartOver={!!sdkDisplayed}
          >
            <StyledBackdrop open={sdkLoading}>
              <CircularProgress color="inherit" size={50} thickness={5} />
            </StyledBackdrop>
            {!sdkDisplayed && (
              <Box position="relative">
                {!pinScreenOpen && (
                  <StyledUserMenuBox>
                    <StoreView />
                    <UserMenu />
                  </StyledUserMenuBox>
                )}
                <TabsControl
                  tabs={{
                    cart: {
                      content: <CreateCartTab />,
                      icons: {
                        active: <CartIcon fontSize="small" />,
                        inactive: <CartInactiveIcon fontSize="small" />,
                      },
                    },
                    ...(consumersTab && {
                      customers: {
                        content: <ConsumersTab />,
                        icons: {
                          active: <ConsumersIcon fontSize="small" />,
                          inactive: <ConsumersInactiveIcon fontSize="small" />,
                        },
                      },
                    }),
                    ...(transactionsTab && {
                      transactions: {
                        content: <TransactionsTab />,
                        icons: {
                          active: <ConsumersIcon fontSize="small" />,
                          inactive: <ConsumersInactiveIcon fontSize="small" />,
                        },
                      },
                    }),
                    ...(displayPendingTab && {
                      pending: {
                        content: <ConsumersTab isPendingTab />,
                        icons: {
                          active: (
                            <Suspense
                              fallback={
                                <StyledBadge
                                  color="error"
                                  badgeContent=""
                                ></StyledBadge>
                              }
                            >
                              <PendingBadge />
                            </Suspense>
                          ),
                        },
                      },
                    }),
                  }}
                />
              </Box>
            )}
          </Page>
        </>
      )}
    </>
  );
};

export default Main;

export const ApprovingLenderLogo: FunctionComponent<{
  lenderId?: string;
}> = ({ lenderId }) => {
  const approvingLender = useGetApprovingLender();
  const chosenLender = lenderId ?? approvingLender;
  const lenderLogo = getImageUrl('lenders', chosenLender, 'logo_default.svg');
  return (
    <img
      data-testid="lender-logo"
      alt="lender-logo"
      src={lenderLogo}
      style={{
        maxHeight: 30,
      }}
    />
  );
};
